import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Alert, Chip, Stack, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, LinearProgress, Divider, Link,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Clear as ClearIcon,
  CheckCircle as OkIcon,
  Warning as WarnIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

/**
 * Generic bulk-import dialog. Drag-drop CSV or Excel, validates each row
 * against a column spec, shows a preview with per-row chips, and uploads
 * the cleaned rows via an injected handler.
 *
 * Props
 *   open               — bool
 *   onClose            — () => void
 *   entityLabel        — singular noun, e.g. "supplier". Used in copy + filenames
 *   entityLabelPlural  — defaults to entityLabel + 's'
 *   mode               — 'insert' (default) | 'update'. In update mode an `id`
 *                        column is required and unknown columns mean partial update
 *   columnSpec         — [{ field, label?, required?, aliases?, type?, enum?, validate? }]
 *                        type: 'string' | 'number' | 'boolean' | 'email'
 *                        validate?: (val, row) => string|null   // null = ok, string = error
 *   sampleRows         — [{ field: value, ... }] used to build the example file
 *   onSubmit           — async (rows) => { inserted?, updated?, failed?, errors?, warning? }
 *                        warning: optional string shown as a non-blocking warning after a successful import
 *                        Called once with the cleaned, error-free rows
 *   maxPreviewRows     — default 50
 *   helpText           — optional extra guidance shown above the dropzone
 */
export default function BulkImportDialog({
  open,
  onClose,
  entityLabel,
  entityLabelPlural,
  mode = 'insert',
  columnSpec = [],
  sampleRows = [],
  onSubmit,
  maxPreviewRows = 50,
  helpText,
}) {
  const plural = entityLabelPlural || `${entityLabel}s`;
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [rawRows, setRawRows] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Reset state whenever dialog reopens
  useEffect(() => {
    if (open) {
      setFileName('');
      setParseError(null);
      setParsing(false);
      setRawRows([]);
      setDragOver(false);
      setSubmitting(false);
      setResult(null);
    }
  }, [open]);

  // === Header detection ===========================================
  const requiredFields = useMemo(
    () => columnSpec.filter((c) => c.required).map((c) => c.field),
    [columnSpec]
  );

  const findHeader = useCallback((headers, target) => {
    const spec = columnSpec.find((c) => c.field === target);
    const aliases = [target, ...(spec?.aliases || [])].map((a) =>
      String(a).toLowerCase().trim()
    );
    const lower = headers.map((h) => String(h || '').toLowerCase().trim());
    for (const a of aliases) {
      const i = lower.indexOf(a);
      if (i !== -1) return headers[i];
    }
    return null;
  }, [columnSpec]);

  // === Coercion + validation =====================================
  const coerce = useCallback((val, spec) => {
    if (val === undefined || val === null || val === '') return '';
    const str = String(val).trim();
    if (spec.type === 'number') {
      const num = parseFloat(str.replace(/[$,£€]/g, ''));
      return Number.isFinite(num) ? num : str;
    }
    if (spec.type === 'boolean') {
      const lo = str.toLowerCase();
      if (['1', 'true', 'yes', 'y', 'active'].includes(lo)) return 1;
      if (['0', 'false', 'no', 'n', 'inactive'].includes(lo)) return 0;
      return str;
    }
    return str;
  }, []);

  const validateRow = useCallback(
    (row, idx, seen) => {
      const errors = [];
      // Mode-specific id requirement
      if (mode === 'update' && (!row.id || String(row.id).trim() === '')) {
        errors.push('Missing id');
      }
      for (const spec of columnSpec) {
        const val = row[spec.field];
        const hasVal = val !== undefined && val !== null && String(val).trim() !== '';
        if (spec.required && !hasVal) {
          errors.push(`Missing ${spec.label || spec.field}`);
          continue;
        }
        if (!hasVal) continue;
        if (spec.type === 'number' && !Number.isFinite(val)) {
          errors.push(`Invalid number for ${spec.label || spec.field}`);
        }
        if (spec.type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(val))) {
          errors.push(`Invalid email for ${spec.label || spec.field}`);
        }
        if (spec.enum && !spec.enum.includes(String(val))) {
          errors.push(`${spec.label || spec.field} must be one of: ${spec.enum.join(', ')}`);
        }
        if (typeof spec.validate === 'function') {
          const v = spec.validate(val, row);
          if (v) errors.push(v);
        }
      }
      // Soft dedup on the first required field
      const dedupKey = requiredFields[0];
      let warning = null;
      if (dedupKey && row[dedupKey]) {
        const k = String(row[dedupKey]).toLowerCase().trim();
        if (seen.has(k)) warning = `Duplicate ${dedupKey}`;
        else seen.add(k);
      }
      if (errors.length) return { level: 'error', label: errors[0], all: errors };
      if (warning) return { level: 'warning', label: warning };
      return { level: 'ok', label: 'OK' };
    },
    [columnSpec, mode, requiredFields]
  );

  const validated = useMemo(() => {
    const seen = new Set();
    return rawRows.map((row, idx) => {
      const v = validateRow(row, idx, seen);
      return { ...row, _validation: v };
    });
  }, [rawRows, validateRow]);

  const stats = useMemo(
    () => ({
      total: validated.length,
      ok: validated.filter((r) => r._validation.level === 'ok').length,
      warn: validated.filter((r) => r._validation.level === 'warning').length,
      err: validated.filter((r) => r._validation.level === 'error').length,
    }),
    [validated]
  );

  // === Parsing ====================================================
  const parseRowsFromObjects = useCallback(
    (objects) => {
      if (!Array.isArray(objects) || objects.length === 0) {
        setParseError('File has no data rows');
        setRawRows([]);
        return;
      }
      const headers = Object.keys(objects[0] || {});
      const headerMap = {};
      const missingRequired = [];
      // Resolve id when in update mode
      if (mode === 'update') {
        const idH =
          headers.find((h) => String(h).toLowerCase().trim() === 'id') ||
          findHeader(headers, 'id');
        if (!idH) missingRequired.push('id');
        else headerMap.id = idH;
      }
      for (const spec of columnSpec) {
        const h = findHeader(headers, spec.field);
        if (h) headerMap[spec.field] = h;
        else if (spec.required && mode === 'insert') missingRequired.push(spec.field);
      }
      if (missingRequired.length) {
        setParseError(
          `Required column(s) not found: ${missingRequired.join(', ')}. Found: ${headers.join(', ')}`
        );
        setRawRows([]);
        return;
      }
      setParseError(null);
      const out = objects.map((o) => {
        const row = {};
        if (headerMap.id) row.id = o[headerMap.id];
        for (const spec of columnSpec) {
          const h = headerMap[spec.field];
          if (!h) continue;
          row[spec.field] = coerce(o[h], spec);
        }
        return row;
      });
      setRawRows(out);
    },
    [columnSpec, coerce, findHeader, mode]
  );

  const handleFile = useCallback(
    (file) => {
      if (!file) return;
      setFileName(file.name);
      setParsing(true);
      setParseError(null);
      setRawRows([]);
      setResult(null);

      const ext = file.name.toLowerCase().split('.').pop();
      if (ext === 'csv' || ext === 'txt') {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (res) => {
            setParsing(false);
            if (res.errors?.length) {
              setParseError(`Parse error: ${res.errors[0].message}`);
              return;
            }
            parseRowsFromObjects(res.data);
          },
          error: (err) => {
            setParsing(false);
            setParseError(`Parse error: ${err.message}`);
          },
        });
      } else if (ext === 'xlsx' || ext === 'xls') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const wb = XLSX.read(e.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            if (!ws) {
              setParseError('Workbook has no sheets');
              setParsing(false);
              return;
            }
            const objects = XLSX.utils.sheet_to_json(ws, { defval: '' });
            parseRowsFromObjects(objects);
          } catch (err) {
            setParseError(`Excel parse error: ${err.message}`);
          } finally {
            setParsing(false);
          }
        };
        reader.onerror = () => {
          setParseError('Failed to read file');
          setParsing(false);
        };
        reader.readAsArrayBuffer(file);
      } else {
        setParsing(false);
        setParseError('Unsupported file type. Use .csv, .xlsx, or .xls');
      }
    },
    [parseRowsFromObjects]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleClear = () => {
    setRawRows([]);
    setFileName('');
    setParseError(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // === Sample download ============================================
  const downloadSample = (format) => {
    const headers =
      mode === 'update'
        ? ['id', ...columnSpec.map((c) => c.field)]
        : columnSpec.map((c) => c.field);
    const rows = sampleRows.length
      ? sampleRows
      : [Object.fromEntries(headers.map((h) => [h, '']))];

    if (format === 'xlsx') {
      const aoa = [headers, ...rows.map((r) => headers.map((h) => r[h] ?? ''))];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, entityLabel);
      XLSX.writeFile(wb, `${plural}-${mode}-template.xlsx`);
      return;
    }
    // CSV default
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => escape(r[h] ?? '')).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${plural}-${mode}-template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // === Submit =====================================================
  const handleSubmit = async () => {
    if (!onSubmit) return;
    const clean = validated
      .filter((r) => r._validation.level !== 'error')
      .map((r) => {
        const { _validation, ...rest } = r;
        return rest;
      });
    if (clean.length === 0) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await onSubmit(clean);
      setResult({ ok: true, ...res });
    } catch (err) {
      setResult({ ok: false, error: err?.response?.data?.error || err.message || 'Upload failed' });
    } finally {
      setSubmitting(false);
    }
  };

  // === Render =====================================================
  const renderChip = (v) => {
    if (v.level === 'ok')
      return <Chip size="small" icon={<OkIcon fontSize="inherit" />} label="OK" color="success" variant="outlined" />;
    if (v.level === 'warning')
      return <Chip size="small" icon={<WarnIcon fontSize="inherit" />} label={v.label} color="warning" variant="outlined" />;
    return (
      <Tooltip title={v.all ? v.all.join('; ') : v.label}>
        <Chip size="small" icon={<ErrorIcon fontSize="inherit" />} label={v.label} color="error" variant="outlined" />
      </Tooltip>
    );
  };

  const previewRows = validated.slice(0, maxPreviewRows);
  const overflow = Math.max(0, validated.length - previewRows.length);
  const displayCols = mode === 'update' ? ['id', ...columnSpec.map((c) => c.field)] : columnSpec.map((c) => c.field);

  const verb = mode === 'update' ? 'Update' : 'Import';

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadIcon color="primary" />
        <Box flexGrow={1}>
          {verb} {plural}
          <Typography variant="caption" display="block" color="text.secondary">
            {mode === 'update'
              ? 'Upload rows with an id column to update existing records. Blank cells are skipped.'
              : `Upload a CSV or Excel file to create ${plural} in bulk.`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {helpText && (
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            {helpText}
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="subtitle2" color="text.secondary">
            Step 1 — download a template (optional)
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadSample('csv')}>
              CSV template
            </Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadSample('xlsx')}>
              Excel template
            </Button>
          </Stack>
        </Stack>

        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Step 2 — upload your file
        </Typography>

        {rawRows.length === 0 ? (
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'divider',
              bgcolor: dragOver ? 'action.hover' : 'background.default',
              borderRadius: 1,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <UploadIcon sx={{ fontSize: 44, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.primary" gutterBottom>
              Drop a <strong>CSV</strong> or <strong>Excel</strong> file here, or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Required columns:{' '}
              {mode === 'update' ? (
                <>
                  <code>id</code>
                  {requiredFields.length > 0 && ` + at least one of: ${requiredFields.map((f) => `'${f}'`).join(', ')}`}
                </>
              ) : requiredFields.length ? (
                requiredFields.map((f) => <code key={f} style={{ marginRight: 6 }}>{f}</code>)
              ) : (
                'none — all columns optional'
              )}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => handleFile(e.target.files?.[0])}
              style={{ display: 'none' }}
            />
          </Box>
        ) : (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
              <Chip size="small" label={fileName} />
              <Chip size="small" label={`${stats.total} rows`} variant="outlined" />
              <Chip size="small" label={`${stats.ok} ready`} color="success" variant="outlined" />
              {stats.warn > 0 && (
                <Chip size="small" label={`${stats.warn} warning`} color="warning" variant="outlined" />
              )}
              {stats.err > 0 && (
                <Chip size="small" label={`${stats.err} error`} color="error" variant="outlined" />
              )}
              <Box flexGrow={1} />
              <Tooltip title="Clear file">
                <IconButton size="small" onClick={handleClear} disabled={submitting}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}>#</TableCell>
                    {displayCols.map((c) => {
                      const spec = columnSpec.find((s) => s.field === c);
                      return (
                        <TableCell key={c}>
                          {spec?.label || c}
                          {spec?.required && mode === 'insert' && (
                            <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>*</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ width: 140 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                      {displayCols.map((c) => (
                        <TableCell
                          key={c}
                          sx={{
                            fontFamily: c === 'id' ? 'monospace' : undefined,
                            fontSize: 12,
                            color: r[c] === '' || r[c] === undefined ? 'text.disabled' : 'inherit',
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r[c] === '' || r[c] === undefined ? '—' : String(r[c])}
                        </TableCell>
                      ))}
                      <TableCell>{renderChip(r._validation)}</TableCell>
                    </TableRow>
                  ))}
                  {overflow > 0 && (
                    <TableRow>
                      <TableCell colSpan={displayCols.length + 2} align="center" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        …and {overflow} more row{overflow === 1 ? '' : 's'} below
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {parsing && <LinearProgress sx={{ mt: 1.5 }} />}
        {parseError && (
          <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setParseError(null)}>
            {parseError}
          </Alert>
        )}
        {result && result.ok && (
          <Alert severity="success" sx={{ mt: 1.5 }}>
            {mode === 'update'
              ? `Updated ${result.updated ?? 0} ${plural}.`
              : `Imported ${result.inserted ?? 0} ${plural}.`}
            {result.failed > 0 && ` ${result.failed} row(s) failed.`}
            {result.errors?.length > 0 && (
              <Box mt={0.5}>
                <Typography variant="caption" component="div" color="error.main">
                  Errors: {result.errors.slice(0, 3).map((e) => e.error || e.message).join('; ')}
                  {result.errors.length > 3 && ` …and ${result.errors.length - 3} more`}
                </Typography>
              </Box>
            )}
          </Alert>
        )}
        {result && result.ok && result.warning && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>{result.warning}</Alert>
        )}
        {result && !result.ok && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{result.error}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {result?.ok ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || stats.ok + stats.warn === 0 || !!result?.ok}
          startIcon={submitting ? null : <UploadIcon />}
        >
          {submitting
            ? 'Uploading…'
            : `${verb} ${stats.ok + stats.warn} row${stats.ok + stats.warn === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
